/**
 * HNS (Hide and Seek) Round Settings Form Component
 * 
 * Provides the settings form for creating/editing Hide and Seek rounds.
 * Extracted from RoundManagement.tsx for better maintainability.
 */

import { useState, useCallback, memo, useMemo } from 'react';
import {
  HnsRoundSettings,
  HnstRoundSettings,
  RoundPlayer,
  HnsRoleRules,
  normalizeHnsRoleRules,
  GameType,
  Player,
  HnsRoundPlayer,
  getPlayerDisplayName,
  Round,
  isHnsRoundPlayer,
  RoundEvent,
  RoundEventKind,
  isRulesetChangePayload,
  builtInHideSeekRoundDefaults,
  hideSeekInitialRoundEvents,
  hideSeekRoundSettingsFromDefaults,
  matchesHideSeekRoundDefaults,
  DEFAULT_HNS_SEEKER_VISIBILITY,
  HNS_MARCO_POLO_RADIUS_MAX,
  HNS_MARCO_POLO_RADIUS_MIN,
  HNS_MAX_CAMERA_DISTANCE_MAX,
  HNS_MAX_CAMERA_DISTANCE_MIN,
  cameraDistancePercent,
  getHnstDeathOutcome,
  getHnstRoundScoring,
  HNST_SECONDS_PER_FIND_DEFAULT,
  HNST_SECONDS_PER_FIND_MAX,
  HNST_SECONDS_PER_FIND_MIN,
  HNST_TEAMS,
  HNST_TEAM_KEYS,
  isHnstTeamKey,
  type GameLocation,
  type HnstTeamKey,
  type HideSeekInitialRoundEvent,
  type HideSeekPublicDisplay,
  type HideSeekRoundDefaults,
  type RoundEventPayload,
  type HnstDeathOutcome,
  type HnstScoringMode,
  type HnsSeekerVisibility,
  type ResolvedGameLocation,
} from '@swtorbit/shared-socket-events';
import { formatDuration } from '@swtorbit/shared-react-hooks';
import { RoundFormBase } from '../round';
import { RoundEventsTimeline, RoundEventDraft, draftToPseudoEvent } from './RoundEventsTimeline';
import { RoleRulesEditor } from './RoleRulesEditor';
import { HnsLocationPicker } from './HnsLocationPicker';
import { RulesDisplayEditor } from './RulesDisplayEditor';

const SEEKER_VISIBILITY_LABELS: Record<HnsSeekerVisibility, string> = {
  admins: 'Admins only',
  seekers: 'Seekers (and admins)',
  everyone: 'Everyone — hiders can see seekers too',
};

/** Where the camera limit starts when an admin switches it on: 50% on the in-game slider. */
const DEFAULT_CAMERA_LIMIT = 14;

function sameLocation(a: GameLocation | null, b: GameLocation | null): boolean {
  return a?.logAreaId === b?.logAreaId && (a?.zoneMapId ?? null) === (b?.zoneMapId ?? null);
}

function locationLabel(location: ResolvedGameLocation): string {
  return location.zone?.name ? `${location.areaName} · ${location.zone.name}` : location.areaName;
}

/** Sort options for the player list */
type PlayerSortOption = 'alpha-asc' | 'alpha-desc' | 'seeker-asc' | 'seeker-desc';

/**
 * Calculate how many times each player was an initial seeker across all rounds
 */
function calculateInitialSeekerCounts(rounds: Round[], playerIds: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  
  // Initialize all players with 0
  for (const playerId of playerIds) {
    counts.set(playerId, 0);
  }
  
  // Count initial seekers from each round
  for (const round of rounds) {
    for (const roundPlayer of round.players) {
      if (!isHnsRoundPlayer(roundPlayer)) continue;
      
      // Use originalRole if available, otherwise fall back to role
      const originalRole = roundPlayer.originalRole || roundPlayer.role;
      if (originalRole === 'seeker') {
        const currentCount = counts.get(roundPlayer.playerId) || 0;
        counts.set(roundPlayer.playerId, currentCount + 1);
      }
    }
  }
  
  return counts;
}

interface HnsRoundSettingsFormProps {
  /**
   * 'hns' (default): a Hide and Seek round, with seeker visibility and the
   * admin's seeker picks. 'hnst': a Hide and Seek Teams round — its hiding
   * team (fixed, or the turn order) and whether it is a Team Captain pick; its
   * players are the teams' members when it starts.
   */
  mode?: 'hns' | 'hnst';
  /** Available players to select from */
  players: Player[];
  /** Game rounds history for calculating seeker counts */
  rounds?: Round[];
  /** Callback when form is submitted */
  onSubmit: (settings: HnsRoundSettings | HnstRoundSettings, players: RoundPlayer[], initialRoundEvents: RoundEventDraft[]) => void;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Initial values for editing (optional) */
  initialSettings?: HnsRoundSettings | HnstRoundSettings;
  /** Initial seeker IDs for editing (optional) */
  initialSeekerIds?: Set<string>;
  /** Existing round events for editing — seeded into the timeline draft list. */
  initialEvents?: RoundEvent[];
  /** Whether this is an edit form */
  isEditing?: boolean;
  /** The game's planned locations, offered first in the location control. */
  locationPlan?: ResolvedGameLocation[];
  /** HNST: the coin toss ("Coin toss 2") the round is linked to, which decides its hiding team; null for none. */
  coinTossLabel?: string | null;
  /**
   * The game's round defaults: a new round starts with them, and an edited
   * round shows whether it follows them and can be reset to them.
   */
  roundDefaults?: HideSeekRoundDefaults;
  /**
   * The form edits the round defaults (given as roundDefaults), not a round:
   * it leaves out each round's own parts (hiding team, location, seekers, note).
   */
  editingDefaults?: boolean;
}

/** Scheduled starting events (a round's, or the defaults') in the draft shape the timeline expects. */
function toDrafts(events: HideSeekInitialRoundEvent[]): RoundEventDraft[] {
  return events.map(e => ({
    kind: e.kind,
    scheduledOffsetSeconds: e.scheduledOffsetSeconds,
    leadTimeSeconds: e.leadTimeSeconds,
    payload: e.kind === RoundEventKind.RULESET_CHANGE && isRulesetChangePayload(e.payload as RoundEventPayload)
      ? e.payload as RoundEventPayload
      : {},
  }));
}

export const HnsRoundSettingsForm = memo(function HnsRoundSettingsForm({
  mode = 'hns',
  players,
  rounds = [],
  onSubmit,
  onCancel,
  initialSettings,
  initialSeekerIds,
  initialEvents,
  isEditing = false,
  locationPlan = [],
  coinTossLabel = null,
  roundDefaults,
  editingDefaults = false,
}: HnsRoundSettingsFormProps) {
  // Where the form starts: the round being edited, or the game's defaults.
  const defaults = useMemo(
    () => roundDefaults ?? builtInHideSeekRoundDefaults(mode === 'hnst' ? GameType.HIDE_AND_SEEK_TEAMS : GameType.HIDE_AND_SEEK),
    [roundDefaults, mode],
  );
  const [start] = useState(() => initialSettings ?? hideSeekRoundSettingsFromDefaults(defaults));

  // Form state
  const [hidingDuration, setHidingDuration] = useState(start.hidingDuration);
  const [roundDuration, setRoundDuration] = useState(start.roundDuration);
  const [hiderRules, setHiderRules] = useState<HnsRoleRules>(() => normalizeHnsRoleRules(start.hiderRules));
  const [seekerRules, setSeekerRules] = useState<HnsRoleRules>(() => normalizeHnsRoleRules(start.seekerRules));
  const [location, setLocation] = useState<GameLocation | null>(start.location);
  // 'detect' | 'plan:<index>' | 'other': how the location control is set.
  const [locationMode, setLocationMode] = useState<string>(() => {
    if (!location) return 'detect';
    const planIndex = locationPlan.findIndex((p) => sameLocation(p, location));
    return planIndex >= 0 ? `plan:${planIndex}` : 'other';
  });
  const [marcoPoloRadius, setMarcoPoloRadius] = useState(start.marcoPoloRadius);
  const [seekerVisibility, setSeekerVisibility] = useState<HnsSeekerVisibility>(
    'seekerVisibility' in start ? start.seekerVisibility : DEFAULT_HNS_SEEKER_VISIBILITY
  );
  // The highest SWTOR Max Camera Distance allowed, as SWTOR saves it (3-25);
  // the slider keeps its place while the limit is off.
  const [limitCamera, setLimitCamera] = useState(start.maxCameraDistance != null);
  const [cameraLimit, setCameraLimit] = useState(start.maxCameraDistance ?? DEFAULT_CAMERA_LIMIT);
  const maxCameraDistance = limitCamera ? cameraLimit : null;
  // HNST only: how the round is scored. The Points-based fields keep their
  // values while Mean-based is chosen.
  const initialScoring = 'hidingTeam' in start ? getHnstRoundScoring(start) : null;
  const [scoringMode, setScoringMode] = useState<HnstScoringMode>(initialScoring?.mode ?? 'mean');
  const [secondsPerFind, setSecondsPerFind] = useState(
    initialScoring?.mode === 'points' ? initialScoring.secondsPerFind : HNST_SECONDS_PER_FIND_DEFAULT
  );
  const [survivorsScore, setSurvivorsScore] = useState(initialScoring?.mode === 'points' && initialScoring.survivorsScore);
  // HNST only: what a hider's death over the Deaths limit does.
  const [deathOutcome, setDeathOutcome] = useState<HnstDeathOutcome>(
    'hidingTeam' in start ? getHnstDeathOutcome(start) : 'disqualified'
  );
  // HNST only: a Team Captain pick's team, and a team the admin fixes (null: the turn order).
  const initialTeams = initialSettings && 'hidingTeam' in initialSettings ? initialSettings : null;
  const [ownerTeam, setOwnerTeam] = useState<HnstTeamKey | null>(initialTeams?.ownerTeam ?? null);
  const [fixedTeam, setFixedTeam] = useState<HnstTeamKey | null>(
    initialTeams && !initialTeams.ownerTeam && initialTeams.coinTossId == null ? initialTeams.hidingTeam : null
  );
  const [selectedSeekers, setSelectedSeekers] = useState<Set<string>>(
    initialSeekerIds ?? new Set()
  );
  const [sortOption, setSortOption] = useState<PlayerSortOption>('alpha-asc');
  const [initialRoundEvents, setInitialRoundEvents] = useState<RoundEventDraft[]>(
    () => toDrafts(initialSettings ? hideSeekInitialRoundEvents(initialEvents) : defaults.roundEvents)
  );
  // What the rules page shows of the round, and the note for players.
  const [publicDisplay, setPublicDisplay] = useState<Partial<HideSeekPublicDisplay>>(start.publicDisplay ?? {});
  const [publicNotes, setPublicNotes] = useState(start.publicNotes ?? '');
  const publicSettings = useMemo(
    () => ({
      ...(Object.keys(publicDisplay).length > 0 && { publicDisplay }),
      ...(publicNotes.trim() && { publicNotes: publicNotes.trim() }),
    }),
    [publicDisplay, publicNotes],
  );

  /** Put the game's defaults back into the form; the round's own settings stay. */
  const resetToDefaults = useCallback(() => {
    setHidingDuration(defaults.hidingDuration);
    setRoundDuration(defaults.roundDuration);
    setHiderRules(normalizeHnsRoleRules(defaults.hiderRules));
    setSeekerRules(normalizeHnsRoleRules(defaults.seekerRules));
    setMarcoPoloRadius(defaults.marcoPoloRadius);
    setLimitCamera(defaults.maxCameraDistance != null);
    if (defaults.maxCameraDistance != null) setCameraLimit(defaults.maxCameraDistance);
    setPublicDisplay(defaults.publicDisplay ?? {});
    setInitialRoundEvents(toDrafts(defaults.roundEvents));
    if (defaults.gameType === GameType.HIDE_AND_SEEK) {
      setSeekerVisibility(defaults.seekerVisibility);
      return;
    }
    setScoringMode(defaults.scoring.mode);
    if (defaults.scoring.mode === 'points') {
      setSecondsPerFind(defaults.scoring.secondsPerFind);
      setSurvivorsScore(defaults.scoring.survivorsScore);
    }
    setDeathOutcome(defaults.deathOutcome);
  }, [defaults]);

  const pseudoEvents = useMemo(
    () => initialRoundEvents.map((d, i) => draftToPseudoEvent(d, i)),
    [initialRoundEvents],
  );

  // Calculate seeker counts for all players
  const seekerCounts = useMemo(() => {
    return calculateInitialSeekerCounts(rounds, players.map(p => p.playerId));
  }, [rounds, players]);

  // Sort players based on selected option
  const sortedPlayers = useMemo(() => {
    const sorted = [...players];
    
    switch (sortOption) {
      case 'alpha-asc':
        sorted.sort((a, b) => {
          const nameA = getPlayerDisplayName(a.playerId, players).toLowerCase();
          const nameB = getPlayerDisplayName(b.playerId, players).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'alpha-desc':
        sorted.sort((a, b) => {
          const nameA = getPlayerDisplayName(a.playerId, players).toLowerCase();
          const nameB = getPlayerDisplayName(b.playerId, players).toLowerCase();
          return nameB.localeCompare(nameA);
        });
        break;
      case 'seeker-asc':
        sorted.sort((a, b) => {
          const countA = seekerCounts.get(a.playerId) || 0;
          const countB = seekerCounts.get(b.playerId) || 0;
          if (countA !== countB) return countA - countB;
          // Secondary sort by name for stability
          const nameA = getPlayerDisplayName(a.playerId, players).toLowerCase();
          const nameB = getPlayerDisplayName(b.playerId, players).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'seeker-desc':
        sorted.sort((a, b) => {
          const countA = seekerCounts.get(a.playerId) || 0;
          const countB = seekerCounts.get(b.playerId) || 0;
          if (countA !== countB) return countB - countA;
          // Secondary sort by name for stability
          const nameA = getPlayerDisplayName(a.playerId, players).toLowerCase();
          const nameB = getPlayerDisplayName(b.playerId, players).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
    }
    
    return sorted;
  }, [players, sortOption, seekerCounts]);

  const settings = useMemo((): HnsRoundSettings | HnstRoundSettings => {
    if (mode === 'hnst') {
      // The hiding team and players are decided when the round starts.
      return {
        gameType: GameType.HIDE_AND_SEEK_TEAMS,
        hidingDuration,
        roundDuration,
        hiderRules,
        seekerRules,
        location,
        marcoPoloRadius,
        maxCameraDistance,
        // The server keeps a toss's rounds and a pick's team as they must be.
        hidingTeam: ownerTeam ?? fixedTeam,
        ownerTeam,
        scoring: scoringMode === 'points'
          ? {
            mode: 'points',
            secondsPerFind: Math.min(HNST_SECONDS_PER_FIND_MAX, Math.max(HNST_SECONDS_PER_FIND_MIN, Math.round(secondsPerFind) || HNST_SECONDS_PER_FIND_DEFAULT)),
            survivorsScore,
          }
          : { mode: 'mean' },
        deathOutcome,
        ...publicSettings,
      };
    }
    return {
      gameType: GameType.HIDE_AND_SEEK,
      hidingDuration,
      roundDuration,
      hiderRules,
      seekerRules,
      location,
      marcoPoloRadius,
      maxCameraDistance,
      seekerVisibility,
      ...publicSettings,
    };
  }, [mode, ownerTeam, fixedTeam, hidingDuration, roundDuration, hiderRules, seekerRules, location, marcoPoloRadius, maxCameraDistance, scoringMode, secondsPerFind, survivorsScore, deathOutcome, seekerVisibility, publicSettings]);

  // Whether the round, as set in the form, follows the game's defaults.
  const followsDefaults = useMemo(
    () => matchesHideSeekRoundDefaults(settings, initialRoundEvents, defaults),
    [settings, initialRoundEvents, defaults],
  );

  const handleSubmit = useCallback(() => {
    if (mode === 'hnst' || editingDefaults) {
      onSubmit(settings, [], initialRoundEvents);
      return;
    }

    const roundPlayers: HnsRoundPlayer[] = players.map((player) => ({
      playerId: player.playerId,
      socketId: player.socketId,
      role: selectedSeekers.has(player.playerId) ? 'seeker' : 'hider',
    }));

    onSubmit(settings, roundPlayers, initialRoundEvents);
  }, [mode, editingDefaults, settings, selectedSeekers, players, initialRoundEvents, onSubmit]);

  const handleLocationMode = useCallback((mode: string) => {
    setLocationMode(mode);
    if (mode === 'detect') setLocation(null);
    else if (mode.startsWith('plan:')) {
      const planned = locationPlan[Number(mode.slice(5))];
      setLocation(planned ? { logAreaId: planned.logAreaId, zoneMapId: planned.zoneMapId } : null);
    }
  }, [locationPlan]);

  const toggleSeeker = useCallback((playerId: string) => {
    setSelectedSeekers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  }, []);

  if (mode === 'hns' && !editingDefaults && players.length === 0) {
    return (
      <RoundFormBase
        playerCount={0}
        isEditing={isEditing}
        onSubmit={handleSubmit}
        onCancel={onCancel}
      >
        {null}
      </RoundFormBase>
    );
  }

  return (
    <RoundFormBase
      playerCount={players.length}
      isEditing={isEditing}
      requiresPlayers={mode === 'hns' && !editingDefaults}
      submitLabel={editingDefaults ? '💾 Save Defaults' : undefined}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      {editingDefaults ? (
        <p className="field__hint">
          New rounds start with these settings. Saving changes the rounds that haven&apos;t started and still follow
          the defaults; Custom rounds keep their own.
        </p>
      ) : followsDefaults ? (
        <p className="field__hint">
          <span className="badge badge--sm badge--aqua">⚙️ Game defaults</span>{' '}
          This round follows the game&apos;s round defaults: changing them changes it until it starts. Changing a
          setting here makes it Custom.
        </p>
      ) : (
        <div className="d-flex flex-wrap align-items-center gap-xs field__hint">
          <span className="badge badge--sm badge--amber">✏️ Custom</span>
          <span>This round keeps its own settings when the game&apos;s round defaults change.</span>
          <button type="button" className="btn btn--sm btn--outline btn--aqua" onClick={resetToDefaults}>
            ↺ Reset to game defaults
          </button>
        </div>
      )}

      {mode === 'hnst' && !editingDefaults && <>
        <p className="field__hint">
          The round&apos;s players are the teams&apos; connected members when it starts.
        </p>
        <div className="field">
          <label htmlFor="hnst-owner-team">Team Captain pick</label>
          <select
            className="select"
            id="hnst-owner-team"
            value={ownerTeam ?? ''}
            disabled={coinTossLabel !== null}
            onChange={(e) => setOwnerTeam(isHnstTeamKey(e.target.value) ? e.target.value : null)}
          >
            <option value="">No — an ordinary round</option>
            {HNST_TEAM_KEYS.map((key) => (
              <option key={key} value={key}>{HNST_TEAMS[key].name}&apos; pick</option>
            ))}
          </select>
          <p className="field__hint fs-xs">
            {coinTossLabel !== null
              ? `Decided by ${coinTossLabel}: remove the round from the toss to change this.`
              : 'A pick is hidden from the other team and the public until you load it. The team that picked it hides in it.'}
          </p>
        </div>
        {!ownerTeam && <div className="field">
          <label htmlFor="hnst-hiding-team">Hiding team</label>
          {coinTossLabel !== null ? (
            <p className="mb-0">{coinTossLabel} decides who hides.</p>
          ) : (
            <select
              className="select"
              id="hnst-hiding-team"
              value={fixedTeam ?? ''}
              onChange={(e) => setFixedTeam(isHnstTeamKey(e.target.value) ? e.target.value : null)}
            >
              <option value="">Turn order — the other team from the round before</option>
              {HNST_TEAM_KEYS.map((key) => (
                <option key={key} value={key}>{HNST_TEAMS[key].name} hide</option>
              ))}
            </select>
          )}
        </div>}
      </>}

      {/* Hiding Duration Slider */}
      <div className="field">
        <label>
          Hiding Duration: <strong>{formatDuration(hidingDuration)}</strong>
        </label>
        <input
          className="range"
          type="range"
          min="5"
          max="300"
          value={hidingDuration}
          onChange={(e) => setHidingDuration(Number(e.target.value))}
        />
        <div className="d-flex justify-content-between fs-xs text-slate">
          <span>5s</span>
          <span>5m</span>
        </div>
      </div>

      {/* Round Duration Slider */}
      <div className="field">
        <label>
          Round Duration: <strong>{formatDuration(roundDuration)}</strong>
        </label>
        <input
          className="range"
          type="range"
          min="0"
          max="1200"
          step="30"
          value={roundDuration}
          onChange={(e) => setRoundDuration(Number(e.target.value))}
        />
        <div className="d-flex justify-content-between fs-xs text-slate">
          <span>0s (Unlimited)</span>
          <span>20m</span>
        </div>
      </div>

      {/* Location: where the round is played (its map), and how positions are shown */}
      {!editingDefaults && <div className="field">
        <label htmlFor="hns-location-mode">Location</label>
        <select className="select" id="hns-location-mode" value={locationMode} onChange={(e) => handleLocationMode(e.target.value)}>
          <option value="detect">Detect from where the hiders are at start</option>
          {locationPlan.map((planned, index) => (
            <option key={`${planned.logAreaId}-${planned.zoneMapId}`} value={`plan:${index}`}>
              {locationLabel(planned)}
            </option>
          ))}
          <option value="other">Another location…</option>
        </select>
        {locationMode === 'other' && (
          <div className="mt-2xs">
            <HnsLocationPicker idPrefix="hns-round-location" value={location} onChange={setLocation} />
          </div>
        )}
      </div>}

      <div className="field">
        <label>
          Marco Polo accuracy: <strong>within {marcoPoloRadius} m</strong>
        </label>
        <input
          className="range"
          type="range"
          min={HNS_MARCO_POLO_RADIUS_MIN}
          max={HNS_MARCO_POLO_RADIUS_MAX}
          step="10"
          value={marcoPoloRadius}
          onChange={(e) => setMarcoPoloRadius(Number(e.target.value))}
        />
        <div className="d-flex justify-content-between fs-xs text-slate">
          <span>{HNS_MARCO_POLO_RADIUS_MIN} m (precise)</span>
          <span>{HNS_MARCO_POLO_RADIUS_MAX} m (vague)</span>
        </div>
      </div>

      {/* Max Camera Distance: the in-game slider (Preferences → Controls), in its own steps */}
      <div className="form-check mt-base">
        <input
          type="checkbox"
          id="hns-limit-camera"
          className="form-check-input"
          checked={limitCamera}
          onChange={(e) => setLimitCamera(e.target.checked)}
        />
        <label htmlFor="hns-limit-camera" className="form-check-label">
          Limit Max Camera Distance
        </label>
        <p className="field__hint fs-xs">
          Players over the limit are asked to lower it in SWTOR (Preferences → Controls) and hold up the start. Raising it
          during the round gives them 15 seconds to lower it again before they are disqualified.
        </p>
      </div>
      {limitCamera && <div className="field">
        <label htmlFor="hns-camera-limit">
          Max Camera Distance: <strong>{cameraDistancePercent(cameraLimit)}%</strong> or less
        </label>
        <input
          className="range"
          type="range"
          id="hns-camera-limit"
          min={HNS_MAX_CAMERA_DISTANCE_MIN}
          max={HNS_MAX_CAMERA_DISTANCE_MAX}
          step="1"
          value={cameraLimit}
          onChange={(e) => setCameraLimit(Number(e.target.value))}
        />
        <div className="d-flex justify-content-between fs-xs text-slate">
          <span>0% (closest)</span>
          <span>100% (furthest)</span>
        </div>
      </div>}

      {mode === 'hnst' && <div className="field">
        <label htmlFor="hnst-scoring-mode">Scoring</label>
        <select
          className="select"
          id="hnst-scoring-mode"
          value={scoringMode}
          onChange={(e) => setScoringMode(e.target.value as HnstScoringMode)}
        >
          <option value="mean">Mean-based — the hiding team scores its hiders&apos; average time</option>
          <option value="points">Points-based — the seeking team scores for each hider found</option>
        </select>
      </div>}
      {mode === 'hnst' && scoringMode === 'points' && <>
        <div className="d-flex flex-wrap gap-sm align-items-end">
          <div className="field">
            <label htmlFor="hnst-seconds-per-find">
              Seconds per hider{secondsPerFind > 0 && <>: <strong>{formatDuration(secondsPerFind)}</strong></>}
            </label>
            <input
              id="hnst-seconds-per-find"
              className="input"
              type="number"
              min={HNST_SECONDS_PER_FIND_MIN}
              max={HNST_SECONDS_PER_FIND_MAX}
              value={secondsPerFind}
              onChange={(e) => setSecondsPerFind(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label htmlFor="hnst-survivors-score">The hiding team scores</label>
            <select
              className="select"
              id="hnst-survivors-score"
              value={survivorsScore ? 'survivors' : 'nothing'}
              onChange={(e) => setSurvivorsScore(e.target.value === 'survivors')}
            >
              <option value="nothing">Nothing</option>
              <option value="survivors">The same for each hider who survives</option>
            </select>
          </div>
        </div>
        <p className="field__hint fs-xs">
          Only hiders a seeker tags count as found. A hider who is disqualified or drops out scores for neither team.
        </p>
      </>}

      {mode === 'hns' && <div className="field">
        <label htmlFor="hns-seeker-visibility">Who sees where the seekers are</label>
        <select
          className="select"
          id="hns-seeker-visibility"
          value={seekerVisibility}
          onChange={(e) => setSeekerVisibility(e.target.value as HnsSeekerVisibility)}
        >
          {(Object.keys(SEEKER_VISIBILITY_LABELS) as HnsSeekerVisibility[]).map((option) => (
            <option key={option} value={option}>{SEEKER_VISIBILITY_LABELS[option]}</option>
          ))}
        </select>
      </div>}

      {/* Role-Specific Rules - Two Column Layout */}
      <RoleRulesEditor
        hiderRules={hiderRules}
        seekerRules={seekerRules}
        onHiderRulesChange={setHiderRules}
        onSeekerRulesChange={setSeekerRules}
      />

      {mode === 'hnst' && <div className="field">
        <label htmlFor="hnst-death-outcome">When a hider dies more than the Deaths rule allows</label>
        <select
          className="select"
          id="hnst-death-outcome"
          value={deathOutcome}
          onChange={(e) => setDeathOutcome(e.target.value as HnstDeathOutcome)}
        >
          <option value="disqualified">Disqualified — they score nothing</option>
          <option value="eliminated">Eliminated — they are out, and the time they hid still counts</option>
        </select>
        <p className="field__hint fs-xs">
          {hiderRules.deaths === 'unlimited'
            ? 'Hiders may die any number of times this round, so this only matters if a rule change limits their deaths.'
            : 'An eliminated hider counts in the team average with the time they hid until that death. In a Points-based round they are neither found nor survived.'}
          {' '}A seeker who dies too often is always disqualified.
        </p>
      </div>}

      {/* Player Selection (Seeker Assignment) */}
      {mode === 'hns' && !editingDefaults && <div className="player-selection">
        <div className="player-selection-header">
          <h4>Assign Seekers (click to toggle)</h4>
          <div className="player-sort-dropdown">
            <label htmlFor="player-sort">Sort by:</label>
            <select
              className="select"
              id="player-sort"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as PlayerSortOption)}
            >
              <option value="alpha-asc">Name (A-Z)</option>
              <option value="alpha-desc">Name (Z-A)</option>
              <option value="seeker-asc">Times Seeker (Low-High)</option>
              <option value="seeker-desc">Times Seeker (High-Low)</option>
            </select>
          </div>
        </div>
        <div className="player-grid">
          {sortedPlayers.map((player) => {
            const seekerCount = seekerCounts.get(player.playerId) || 0;
            const seekerCountText = seekerCount === 1 ? 'x1 time' : `x${seekerCount} times`;
            
            return (
              <button
                key={player.playerId}
                onClick={() => toggleSeeker(player.playerId)}
                className={`btn btn--outline btn--block justify-content-start ${selectedSeekers.has(player.playerId) ? 'btn--aqua' : 'btn--pink'}`}
              >
                <div className="player-info">
                  <span className="player-name">{getPlayerDisplayName(player.playerId, players)}</span>
                  <span className="seeker-count">(Seeker {seekerCountText})</span>
                </div>
                <span className="role-indicator">
                  {selectedSeekers.has(player.playerId) ? '🔍 SEEKER' : '🙈 HIDER'}
                </span>
              </button>
            );
          })}
        </div>
      </div>}

      {/* Round Events Timeline (initial scheduled events) */}
      <RoundEventsTimeline
        mode="setup"
        events={pseudoEvents}
        baselineHiderRules={hiderRules}
        baselineSeekerRules={seekerRules}
        onSchedule={(draft) => setInitialRoundEvents(prev => [...prev, draft])}
        onDelete={(id) => setInitialRoundEvents(prev => prev.filter((_, i) => pseudoEvents[i]?.id !== id))}
      />

      <RulesDisplayEditor
        gameType={mode === 'hnst' ? GameType.HIDE_AND_SEEK_TEAMS : GameType.HIDE_AND_SEEK}
        display={publicDisplay}
        notes={editingDefaults ? undefined : publicNotes}
        onDisplayChange={setPublicDisplay}
        onNotesChange={editingDefaults ? undefined : setPublicNotes}
      />

      {/* Actions */}
      </RoundFormBase>
  );
});
